import { memo, useEffect, useRef } from "react";
import type { TextSegment } from "@/Utils/message";
import "./Bubble.scss";

export type { TextSegment };

export interface BubbleData {
    messageid: string;
    type: "text" | "emoji" | "images" | "card";
    text?: string;
    segments?: TextSegment[];
    images?: string[];
    isLastBubble?: boolean;
    timestamp?: number;
    showSenderInfo?: boolean;
    role?: "user" | "assistant";
}

interface BubbleProps {
    bubbleData: BubbleData;
    position?: "left" | "right";
    onLongPress?: (messageid: string) => void;
}

const Bubble = memo(({ bubbleData, position = "left", onLongPress }: BubbleProps) => {
    const containerRef = useRef<HTMLDivElement>(null);

    const { messageid, type, text, segments, images, showSenderInfo } = bubbleData;

    // Fade-in animation
    useEffect(() => {
        if (containerRef.current) {
            containerRef.current.style.opacity = "1";
        }
    }, []);

    // Check if single styled segment (transparent background)
    const isSingleStyledSegment =
        segments?.length === 1 && segments[0] && (segments[0].bold || segments[0].italic);

    const isEmojiOnly = type === "emoji" || (segments?.length === 1 && segments[0]?.emojiOnly);

    const handleUrlClick = (url: string) => {
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const handleContextMenu = (e: React.MouseEvent) => {
        if (onLongPress) {
            e.preventDefault();
            onLongPress(messageid);
        }
    };

    return (
        <div className="BubbleWrapper" ref={containerRef}>
            {/* Images bubble */}
            {type === "images" && images && images.length > 0 && (
                <div className={`BubbleImages ${position === "left" ? "Left" : "Right"}`}>
                    <div
                        className={`ImageGrid ${
                            images.length === 1
                                ? "Single"
                                : images.length === 2
                                  ? "Double"
                                  : images.length === 3
                                    ? "Triple"
                                    : "Quad"
                        }`}
                    >
                        {images.map((imageUrl, index) => (
                            <div
                                key={index}
                                className="GridItem"
                                onContextMenu={handleContextMenu}
                            >
                                <img src={imageUrl} alt="" />
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Text bubble */}
            {(type === "text" || type === "emoji") && text && (
                <div className={`BubbleContainer ${position === "left" ? "Left" : "Right"}`}>
                    <div
                        className={`Bubble ${position === "left" ? "Their" : "Own"} ${
                            showSenderInfo && position === "left" ? "WithAvatar" : ""
                        } ${isEmojiOnly || isSingleStyledSegment ? "Transparent" : ""}`}
                        onContextMenu={handleContextMenu}
                    >
                        <span className="BubbleText">
                            {segments?.map((segment, idx) =>
                                segment.isUrl ? (
                                    <a
                                        key={idx}
                                        className="UrlText"
                                        href={segment.text}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleUrlClick(segment.text);
                                        }}
                                    >
                                        {segment.text}
                                    </a>
                                ) : (
                                    <span
                                        key={idx}
                                        className={[
                                            segment.bold && "Bold",
                                            segment.italic && "Italic",
                                            (segment.bold || segment.italic) &&
                                                segments.length === 1 &&
                                                "StyledSingle",
                                            segment.emojiOnly && segments.length === 1 && "EmojiOnly",
                                        ]
                                            .filter(Boolean)
                                            .join(" ")}
                                    >
                                        {segment.text}
                                    </span>
                                )
                            )}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
});

Bubble.displayName = "Bubble";

export default Bubble;
